/* eslint-disable max-lines-per-function, max-statements */
import getServiceBatches from "helpers/getServiceBatches";
import useClearNewsListButton from "hooks/actions/useClearNewsListButton";
import useListButton from "hooks/actions/useListButton";
import useLoadMoreButton from "hooks/actions/useLoadMoreButton";
import { useCallback, useMemo, useState } from "react";
const NEWS_LIST_BATCH_SIZE = 2;
/**
 * useNewsServiceListControls
 *
 * @description
 * Builds per-service News list action props and coordinates the
 * global List All / Clear All Lists controls.
 *
 * @param {readonly NewsServiceListItem[]} newsServiceNames -
 * News services used to build control props.
 *
 * @returns {UseNewsServiceListControls} -
 * List, clear, load-more, and global list action props.
 *
 */
const useNewsServiceListControls = (newsServiceNames) => {
    const [isClearAllListsLoading, setIsClearAllListsLoading] = useState(false);
    const [isListAllLoading, setIsListAllLoading] = useState(false);
    const isListControlsLoading = isClearAllListsLoading || isListAllLoading;
    const rawListButtonProps = newsServiceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useListButton({
            serviceName,
            serviceType: "news"
        });
        return acc;
    }, {});
    const listButtonProps = useMemo(() => newsServiceNames.reduce((acc, { serviceName }) => {
        const listButton = rawListButtonProps[serviceName];
        acc[serviceName] = {
            ...listButton,
            isDisabled: listButton.isDisabled || isListControlsLoading
        };
        return acc;
    }, {}), [isListControlsLoading, newsServiceNames, rawListButtonProps]);
    const clearListButtonProps = newsServiceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useClearNewsListButton({
            isDisabled: isListControlsLoading,
            isLoading: rawListButtonProps[serviceName].loading,
            serviceName
        });
        return acc;
    }, {});
    const servicesToList = useMemo(() => newsServiceNames
        .map(({ serviceName }) => serviceName)
        .filter((serviceName) => !rawListButtonProps[serviceName].isDisabled), [newsServiceNames, rawListButtonProps]);
    const serviceBatches = useMemo(() => getServiceBatches(servicesToList, NEWS_LIST_BATCH_SIZE), [servicesToList]);
    const servicesToClear = useMemo(() => newsServiceNames
        .map(({ serviceName }) => serviceName)
        .filter((serviceName) => !clearListButtonProps[serviceName].isDisabled), [clearListButtonProps, newsServiceNames]);
    const clearServiceBatches = useMemo(() => getServiceBatches(servicesToClear, NEWS_LIST_BATCH_SIZE), [servicesToClear]);
    const onListAll = useCallback(() => {
        if (isListControlsLoading || !servicesToList.length) {
            return;
        }
        setIsListAllLoading(true);
        const listAllPromise = serviceBatches.reduce((chain, serviceBatch) => chain.then(() => Promise.allSettled(serviceBatch.map((serviceName) => Promise.resolve(rawListButtonProps[serviceName].onClick()))).then(() => undefined)), Promise.resolve());
        listAllPromise.finally(() => {
            setIsListAllLoading(false);
        });
    }, [
        isListControlsLoading,
        rawListButtonProps,
        serviceBatches,
        servicesToList.length
    ]);
    const onClearAllLists = useCallback(() => {
        if (isListControlsLoading || !servicesToClear.length) {
            return;
        }
        setIsClearAllListsLoading(true);
        const clearAllPromise = clearServiceBatches.reduce((chain, serviceBatch) => chain.then(() => Promise.allSettled(serviceBatch.map((serviceName) => Promise.resolve(clearListButtonProps[serviceName].onClick()))).then(() => undefined)), Promise.resolve());
        clearAllPromise.finally(() => {
            setIsClearAllListsLoading(false);
        });
    }, [
        clearListButtonProps,
        clearServiceBatches,
        isListControlsLoading,
        servicesToClear.length
    ]);
    const listAllButtonProps = useMemo(() => ({
        isDisabled: isListControlsLoading || !servicesToList.length,
        label: "List All",
        loading: isListAllLoading,
        onClick: onListAll
    }), [isListAllLoading, isListControlsLoading, onListAll, servicesToList.length]);
    const clearAllListsButtonProps = useMemo(() => ({
        isDisabled: isListControlsLoading || !servicesToClear.length,
        label: "Clear All Lists",
        loading: isClearAllListsLoading,
        onClick: onClearAllLists,
        secondary: true
    }), [
        isClearAllListsLoading,
        isListControlsLoading,
        onClearAllLists,
        servicesToClear.length
    ]);
    const loadMoreButtonProps = newsServiceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useLoadMoreButton({
            serviceName,
            serviceType: "news"
        });
        return acc;
    }, {});
    return {
        clearAllListsButtonProps,
        clearListButtonProps,
        listAllButtonProps,
        listButtonProps,
        loadMoreButtonProps
    };
};
export default useNewsServiceListControls;
